import React, { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, FormGroup, FormControlLabel, Checkbox, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Grid, MenuItem, Alert } from '@mui/material';
import LanguageSwitcher from './LanguageSwitcher';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { getContent } from '../utils/contentLoader';
import { getActivePractices } from '../services/patientService';
import { saveConsentData, sendConsentEmails } from '../services/consentService';
import useDobConfirm from '../hooks/useDobConfirm';
import DobConfirmDialog from './DobConfirmDialog';

export default function TreatmentConsents({ onSubmit }) {
  const { t, i18n } = useTranslation('treatmentConsents');
  
  // Load language-aware content
  const privacyPolicy = useMemo(() => getContent('privacyPolicy', i18n.language), [i18n.language]);
  const noticeOfPrivacyPractices = useMemo(() => getContent('noticeOfPrivacyPractices', i18n.language), [i18n.language]);
  const financialResponsibility = useMemo(() => getContent('financialResponsibility', i18n.language), [i18n.language]);
  const recordsRelease = useMemo(() => getContent('recordsRelease', i18n.language), [i18n.language]);
  const imageryTreatmentEducation = useMemo(() => getContent('imageryTreatmentEducation', i18n.language), [i18n.language]);
  const imagerySocialMedia = useMemo(() => getContent('imagerySocialMedia', i18n.language), [i18n.language]);
  const authorizedRepresentativesText = useMemo(() => getContent('authorizedRepresentatives', i18n.language), [i18n.language]);
  
  const [consents, setConsents] = useState({
    privacyPolicy: false,
    noticeOfPrivacyPractices: false,
    financialResponsibility: false,
    recordsRelease: false,
    imageryTreatmentEducation: false,
    imagerySocialMedia: false,
    authorizedRepresentatives: false,
  });
  // Identification fields
  const [practice, setPractice] = useState('');
  const [practiceCode, setPracticeCode] = useState('');
  const [patientName, setPatientName] = useState('');
  const [dob, setDobRaw] = useState('');
  const [gender, setGender] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [email, setEmail] = useState('');
  const [date, setDate] = useState('');
  const [practices, setPractices] = useState([]);
  const [practicesLoading, setPracticesLoading] = useState(true);
  const [practicesError, setPracticesError] = useState(null);
  const [lockedPractice, setLockedPractice] = useState(null);
  // DOB confirmation
  const { dobInputRef, showDobConfirmDialog, handleDobBlur, handleDobChange: setDob, confirmDob, fixDob } = useDobConfirm(dob, setDobRaw);

  // Wet signature
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  // Privacy policy modal state
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [privacyInitials, setPrivacyInitials] = useState('');
  const [privacyForbiddenName, setPrivacyForbiddenName] = useState('');
  const [privacyScrolled, setPrivacyScrolled] = useState(false);
  const policyRef = useRef(null);
  const [privacyDisagree, setPrivacyDisagree] = useState(false);
  // Financial responsibility modal state
  const [financialOpen, setFinancialOpen] = useState(false);
  const [financialInitials, setFinancialInitials] = useState('');
  const [financialScrolled, setFinancialScrolled] = useState(false);
  const financialRef = useRef(null);
  const [financialDisagree, setFinancialDisagree] = useState(false);
  // Records release modal state
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [recordsInitials, setRecordsInitials] = useState('');
  const [recordsScrolled, setRecordsScrolled] = useState(false);
  const recordsRef = useRef(null);
  const [recordsDisagree, setRecordsDisagree] = useState(false);
  // Imagery (treatment/education) modal state
  const [imageryEduOpen, setImageryEduOpen] = useState(false);
  const [imageryEduInitials, setImageryEduInitials] = useState('');
  const [imageryEduScrolled, setImageryEduScrolled] = useState(false);
  const imageryEduRef = useRef(null);
  const [imageryEduDisagree, setImageryEduDisagree] = useState(false);
  // Imagery (social media/other purposes) modal state
  const [imagerySocialOpen, setImagerySocialOpen] = useState(false);
  const [imagerySocialInitials, setImagerySocialInitials] = useState('');
  const [imagerySocialScrolled, setImagerySocialScrolled] = useState(false);
  const imagerySocialRef = useRef(null);
  const [imagerySocialDisagree, setImagerySocialDisagree] = useState(false);
  const [imagerySocialDeidentified, setImagerySocialDeidentified] = useState(false);
  const [imagerySocialRequiresScroll, setImagerySocialRequiresScroll] = useState(true);
  // Authorized representatives modal state
  const [authRepOpen, setAuthRepOpen] = useState(false);
  const [authRepInitials, setAuthRepInitials] = useState('');
  const [authRepScrolled, setAuthRepScrolled] = useState(false);
  const authRepRef = useRef(null);
  const [authRepDisagree, setAuthRepDisagree] = useState(false);
  // Notice of Privacy Practices modal state
  const [noticePrivacyOpen, setNoticePrivacyOpen] = useState(false);
  const [noticePrivacyInitials, setNoticePrivacyInitials] = useState('');
  const [noticePrivacyScrolled, setNoticePrivacyScrolled] = useState(false);
  const noticePrivacyRef = useRef(null);
  const [noticePrivacyDisagree, setNoticePrivacyDisagree] = useState(false);
  // Submission state
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Disagree confirmation dialog (MUI) for key consents
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmContext, setConfirmContext] = useState(null); // 'privacy'|'noticePrivacy'|'financial'|'records'|'imageryEdu'|'authRep'
  const openConfirm = (ctx) => { setConfirmContext(ctx); setConfirmOpen(true); };
  const closeConfirm = () => { setConfirmOpen(false); setConfirmContext(null); };
  const handleConfirmDisagree = () => {
    switch (confirmContext) {
      case 'privacy':
        setPrivacyDisagree(true);
        closePrivacyModal();
        break;
      case 'noticePrivacy':
        setNoticePrivacyDisagree(true);
        closeNoticePrivacyModal();
        break;
      case 'financial':
        setFinancialDisagree(true);
        closeFinancialModal();
        break;
      case 'records':
        setRecordsDisagree(true);
        closeRecordsModal();
        break;
      case 'imageryEdu':
        setImageryEduDisagree(true);
        closeImageryEduModal();
        break;
      case 'authRep':
        setAuthRepDisagree(true);
        closeAuthRepModal();
        break;
      default:
        break;
    }
    closeConfirm();
  };

  const openPrivacyModal = () => {
    setPrivacyOpen(true);
    setPrivacyInitials('');
    setPrivacyScrolled(false);
  };

  const closePrivacyModal = () => {
    setPrivacyOpen(false);
    // Ensure checkbox stays unchecked if dialog closed without accept
    setConsents((prev) => ({ ...prev, privacyPolicy: false }));
  };

  const handlePolicyScroll = () => {
    const el = policyRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8; // small buffer
    if (atBottom) setPrivacyScrolled(true);
  };

  const openFinancialModal = () => {
    setFinancialOpen(true);
    setFinancialInitials('');
    setFinancialScrolled(false);
  };

  const closeFinancialModal = () => {
    setFinancialOpen(false);
    setConsents((prev) => ({ ...prev, financialResponsibility: false }));
  };

  const handleFinancialScroll = () => {
    const el = financialRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atBottom) setFinancialScrolled(true);
  };

  const openRecordsModal = () => {
    setRecordsOpen(true);
    setRecordsInitials('');
    setRecordsScrolled(false);
  };

  const closeRecordsModal = () => {
    setRecordsOpen(false);
    setConsents((prev) => ({ ...prev, recordsRelease: false }));
  };

  const handleRecordsScroll = () => {
    const el = recordsRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atBottom) setRecordsScrolled(true);
  };

  const openImageryEduModal = () => {
    setImageryEduOpen(true);
    setImageryEduInitials('');
    setImageryEduScrolled(false);
  };

  const closeImageryEduModal = () => {
    setImageryEduOpen(false);
    setConsents((prev) => ({ ...prev, imageryTreatmentEducation: false }));
  };

  const handleImageryEduScroll = () => {
    const el = imageryEduRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atBottom) setImageryEduScrolled(true);
  };

  const openImagerySocialModal = () => {
    setImagerySocialOpen(true);
    setImagerySocialInitials('');
    setImagerySocialScrolled(false);
    setImagerySocialDeidentified(false);
    setImagerySocialRequiresScroll(true);
  };

  const closeImagerySocialModal = () => {
    setImagerySocialOpen(false);
    setConsents((prev) => ({ ...prev, imagerySocialMedia: false }));
    setImagerySocialDisagree(true);
    setImagerySocialDeidentified(false);
  };

  const handleImagerySocialScroll = () => {
    const el = imagerySocialRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atBottom) setImagerySocialScrolled(true);
  };

  // If the social media consent content doesn't overflow, enable acceptance without scrolling
  React.useEffect(() => {
    if (!imagerySocialOpen) return;
    const check = () => {
      const el = imagerySocialRef.current;
      if (!el) return;
      const fits = el.scrollHeight <= el.clientHeight + 1; // small buffer for rounding
      setImagerySocialRequiresScroll(!fits);
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
      if (fits || atBottom) setImagerySocialScrolled(true);
    };
    // Defer check until after content/layout has painted
    const raf = requestAnimationFrame(() => setTimeout(check, 0));
    const t1 = setTimeout(check, 50);
    const t2 = setTimeout(check, 200);
    window.addEventListener('resize', check);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', check);
    };
  }, [imagerySocialOpen, practice]);

  const openAuthRepModal = () => {
    setAuthRepOpen(true);
    setAuthRepInitials('');
    setAuthRepScrolled(false);
  };

  const closeAuthRepModal = () => {
    setAuthRepOpen(false);
    setConsents((prev) => ({ ...prev, authorizedRepresentatives: false }));
  };

  const handleAuthRepScroll = () => {
    const el = authRepRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atBottom) setAuthRepScrolled(true);
  };

  const openNoticePrivacyModal = () => {
    setNoticePrivacyOpen(true);
    setNoticePrivacyInitials('');
    setNoticePrivacyScrolled(false);
  };

  const closeNoticePrivacyModal = () => {
    setNoticePrivacyOpen(false);
    setConsents((prev) => ({ ...prev, noticeOfPrivacyPractices: false }));
  };

  const handleNoticePrivacyScroll = () => {
    const el = noticePrivacyRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atBottom) setNoticePrivacyScrolled(true);
  };

  const handleChange = (key) => (e) => {
    setConsents((prev) => ({ ...prev, [key]: e.target.checked }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const nowIso = new Date().toISOString();
    const payload = {
      // Identification
      practiceName: practice,
      practiceCode: practiceCode || null,
      name: patientName,
      birthdate: dob,
      gender,
      phone_number: formatPhone(cellPhone),
      email,
      dateSigned: date,
      // Consents (booleans and metadata; server will normalize to strings for DB)
      consents: {
        privacyPolicy: consents.privacyPolicy,
        noticeOfPrivacyPractices: consents.noticeOfPrivacyPractices,
        financialResponsibility: consents.financialResponsibility,
        recordsRelease: consents.recordsRelease,
        imageryTreatmentEducation: consents.imageryTreatmentEducation,
        imagerySocialMedia: consents.imagerySocialMedia,
        authorizedRepresentatives: consents.authorizedRepresentatives,
      },
      consentsMeta: {
        privacyPolicy: { initials: privacyInitials || null, timestamp: consents.privacyPolicy ? nowIso : null, forbiddenRelease: privacyForbiddenName || null },
        noticeOfPrivacyPractices: { initials: noticePrivacyInitials || null, timestamp: consents.noticeOfPrivacyPractices ? nowIso : null },
        financialResponsibility: { initials: financialInitials || null, timestamp: consents.financialResponsibility ? nowIso : null },
        recordsRelease: { initials: recordsInitials || null, timestamp: consents.recordsRelease ? nowIso : null },
        imageryTreatmentEducation: { initials: imageryEduInitials || null, timestamp: consents.imageryTreatmentEducation ? nowIso : null },
        imagerySocialMedia: { initials: imagerySocialInitials || null, timestamp: consents.imagerySocialMedia ? nowIso : null, deidentified: imagerySocialDeidentified },
        authorizedRepresentatives: { initials: authRepInitials || null, timestamp: consents.authorizedRepresentatives ? nowIso : null },
      },
      signature: {
        image: signatureDataUrl || null,
        signedAt: nowIso,
      }
    };
    try {
      const saveRes = await saveConsentData(payload);
      if (!saveRes.success) throw new Error(saveRes.error || 'Failed to save consents');
      const emailRes = await sendConsentEmails(payload);
      if (!emailRes.success) {
        alert('Consents saved, but failed to send emails.');
      }
      setSubmitSuccess(true);
      if (onSubmit) onSubmit(payload);
    } catch (err) {
      console.error('Consent submission failed:', err);
      alert(`Failed to submit consents: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load practices and pre-populate from URL parameters (same approach as Step1_PatientInfo)
  React.useEffect(() => {
    const loadPractices = async () => {
      try {
        setPracticesLoading(true);
        const activePractices = await getActivePractices();
        setPractices(activePractices);
        setPracticesError(null);
      } catch (error) {
        console.error('Failed to load practices:', error);
        setPracticesError('Failed to load practices');
        // Fallback to hardcoded practices if API fails
        setPractices([
          { practice_name: 'STARS Plastic Surgery' },
          { practice_name: 'San Antonio General Surgery' }
        ]);
      } finally {
        setPracticesLoading(false);
      }
    };
    loadPractices();

    // Pre-populate fields from URL parameters if present
    const urlParams = new URLSearchParams(window.location.search);
    const practiceParam = urlParams.get('practice');
    const nameParam = urlParams.get('name');
    const dobParam = urlParams.get('dob');
    const genderParam = urlParams.get('gender');
    const phoneParam = urlParams.get('phone');
    const emailParam = urlParams.get('email');

    if (practiceParam) {
      setPractice(practiceParam);
      setLockedPractice(practiceParam); // Lock the practice when pre-selected
    }
    if (nameParam) setPatientName(nameParam);
    if (dobParam) setDob(dobParam);
    if (genderParam) setGender(genderParam);
    if (phoneParam) setCellPhone(phoneParam);
    if (emailParam) setEmail(emailParam);
  }, []);

  // Prefill signature date with today if empty
  React.useEffect(() => {
    if (!date) {
      const today = new Date();
      const iso = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))
        .toISOString()
        .slice(0, 10);
      setDate(iso);
    }
  }, [date]);

  // Derive practiceCode when practice selection changes
  React.useEffect(() => {
    if (!practice) {
      setPracticeCode('');
      return;
    }
    const match = (practices || []).find((p) => p.practice_name === practice);
    setPracticeCode(match?.practice_code || '');
  }, [practice, practices]);

  // Helpers: phone formatting and validation (match Step1_PatientInfo.jsx)
  const onlyDigits = (s) => (s || '').replace(/\D/g, '');
  const formatPhone = (digits) => {
    const d = onlyDigits(digits).slice(0, 10);
    if (!d) return '';
    return d.replace(/^(\d{3})(\d{0,3})(\d{0,4})$/, (m, a, b, c) => {
      let out = a;
      if (b) out += '-' + b;
      if (c) out += '-' + c;
      return out;
    });
  };
  const handlePhoneChange = (e) => {
    // Store digits-only, format only for display (match Step1)
    const cleaned = e.target.value.replace(/\D/g, '');
    setCellPhone(cleaned);
  };
  const isValidPhone = cellPhone && cellPhone.length === 10;
  // Use same email regex as Step1
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(email);

  const idComplete = Boolean(
    practice && patientName && dob && gender && isValidPhone && isValidEmail
  );
  const hasSignature = Boolean(signatureDataUrl);
  // Require all consents to be either explicitly agreed (checkbox true) or explicitly disagreed (sad-face shown)
  const allReviewed = Boolean(
    (consents.privacyPolicy || privacyDisagree) &&
    (consents.noticeOfPrivacyPractices || noticePrivacyDisagree) &&
    (consents.financialResponsibility || financialDisagree) &&
    (consents.recordsRelease || recordsDisagree) &&
    (consents.imageryTreatmentEducation || imageryEduDisagree) &&
    (consents.imagerySocialMedia || imagerySocialDisagree) &&
    (consents.authorizedRepresentatives || authRepDisagree)
  );

  return (
    <Box>
      {submitSuccess ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('successMessage', { email })}
        </Alert>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <LanguageSwitcher />
          </Box>
          <Typography variant="h5" align="center" gutterBottom>
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
            {t('subtitle')}
          </Typography>

          {/* Identification Section */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('sections.patientIdentification')}
            </Typography>
            <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label={t('labels.practice')}
              value={practice}
              onChange={(e) => !lockedPractice && setPractice(e.target.value)}
              disabled={practicesLoading || !!lockedPractice}
              error={!!practicesError}
              helperText={
                practicesError ? t('errors.fallbackList') : 
                (lockedPractice ? 'Practice pre-selected from your link' : '')
              }
            >
              {(practices || []).map((p) => (
                <MenuItem key={p.practice_name} value={p.practice_name}>{p.practice_name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label={t('labels.fullName')} value={patientName} onChange={(e) => setPatientName(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label={t('labels.dateOfBirth')} InputLabelProps={{ shrink: true }} inputRef={dobInputRef} value={dob} onChange={(e) => setDob(e.target.value)} onBlur={handleDobBlur} />
            <DobConfirmDialog open={showDobConfirmDialog} dob={dob} onFix={fixDob} onConfirm={confirmDob} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label={t('labels.gender')} value={gender} onChange={(e) => setGender(e.target.value)}>
              <MenuItem value="Male">{t('genderOptions.male')}</MenuItem>
              <MenuItem value="Female">{t('genderOptions.female')}</MenuItem>
              <MenuItem value="Other">{t('genderOptions.other')}</MenuItem>
              <MenuItem value="Prefer not to say">{t('genderOptions.preferNotToSay')}</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('labels.cellPhone')}
              value={
                cellPhone
                  ? cellPhone.replace(/^(\d{3})(\d{0,3})(\d{0,4})$/, (match, p1, p2, p3) => {
                      let formatted = p1;
                      if (p2) formatted += '-' + p2;
                      if (p3) formatted += '-' + p3;
                      return formatted;
                    })
                  : ''
              }
              onChange={handlePhoneChange}
              placeholder={t('placeholders.phone')}
              error={cellPhone && cellPhone.length !== 10}
              helperText={cellPhone && cellPhone.length !== 10 ? t('errors.validPhone') : ''}
              inputMode="tel"
              inputProps={{ maxLength: 12 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="email"
              label={t('labels.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('placeholders.email')}
              error={email && !isValidEmail}
              helperText={email && !isValidEmail ? t('errors.validEmail') : ''}
              inputMode="email"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.privacyPolicy}
                onChange={(e) => {
                  // If checking, open modal first and don't set true yet
                  if (e.target.checked) {
                    openPrivacyModal();
                  } else {
                    setConsents((prev) => ({ ...prev, privacyPolicy: false }));
                  }
                }}
                disabled={!idComplete}
                icon={privacyDisagree ? <SentimentDissatisfiedIcon sx={{ color: 'error.main' }} /> : undefined}
              />
            }
            label={t('consents.privacyPolicy')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.noticeOfPrivacyPractices}
                onChange={(e) => {
                  if (e.target.checked) {
                    openNoticePrivacyModal();
                  } else {
                    setConsents((prev) => ({ ...prev, noticeOfPrivacyPractices: false }));
                  }
                }}
                disabled={!idComplete}
                icon={noticePrivacyDisagree ? <SentimentDissatisfiedIcon sx={{ color: 'error.main' }} /> : undefined}
              />
            }
            label={t('consents.noticeOfPrivacyPractices')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.financialResponsibility}
                onChange={(e) => {
                  if (e.target.checked) {
                    openFinancialModal();
                  } else {
                    setConsents((prev) => ({ ...prev, financialResponsibility: false }));
                  }
                }}
                disabled={!idComplete}
                icon={financialDisagree ? <SentimentDissatisfiedIcon sx={{ color: 'error.main' }} /> : undefined}
              />
            }
            label={t('consents.financialResponsibility')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.recordsRelease}
                onChange={(e) => {
                  if (e.target.checked) {
                    openRecordsModal();
                  } else {
                    setConsents((prev) => ({ ...prev, recordsRelease: false }));
                  }
                }}
                disabled={!idComplete}
                icon={recordsDisagree ? <SentimentDissatisfiedIcon sx={{ color: 'error.main' }} /> : undefined}
              />
            }
            label={t('consents.recordsRelease')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.imageryTreatmentEducation}
                onChange={(e) => {
                  if (e.target.checked) {
                    openImageryEduModal();
                  } else {
                    setConsents((prev) => ({ ...prev, imageryTreatmentEducation: false }));
                  }
                }}
                disabled={!idComplete}
                icon={imageryEduDisagree ? <SentimentDissatisfiedIcon sx={{ color: 'error.main' }} /> : undefined}
              />
            }
            label={t('consents.imageryTreatmentEducation')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.imagerySocialMedia}
                onChange={(e) => {
                  if (e.target.checked) {
                    openImagerySocialModal();
                  } else {
                    setConsents((prev) => ({ ...prev, imagerySocialMedia: false }));
                  }
                }}
                disabled={!idComplete}
                icon={imagerySocialDisagree ? <SentimentDissatisfiedIcon sx={{ color: 'error.main' }} /> : undefined}
              />
            }
            label={t('consents.imagerySocialMedia')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={consents.authorizedRepresentatives}
                onChange={(e) => {
                  if (e.target.checked) {
                    openAuthRepModal();
                  } else {
                    setConsents((prev) => ({ ...prev, authorizedRepresentatives: false }));
                  }
                }}
                disabled={!idComplete}
                icon={authRepDisagree ? <SentimentDissatisfiedIcon sx={{ color: 'error.main' }} /> : undefined}
              />
            }
            label={t('consents.authorizedRepresentatives', { practice: practice || '[Medical practice]' })}
          />
        </FormGroup>
      </Paper>

      {/* Privacy Policy Modal */}
      <Dialog open={privacyOpen} onClose={closePrivacyModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('dialogs.privacyPolicy.title')}</DialogTitle>
        <DialogContent dividers>
          <Box
            ref={policyRef}
            onScroll={handlePolicyScroll}
            sx={{ maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap' }}
          >
            {(() => {
              const raw = (privacyPolicy?.policy || []).join('\n\n');
              const name = practice || '[Medical practice]';
              return raw.replace(/\[Medical practice\]|\[Medical Practice\]/g, name);
            })()}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color={privacyScrolled ? 'success.main' : 'text.secondary'}>
              {privacyScrolled ? t('scrollMessages.reachedEnd', { type: t('scrollMessages.policy') }) : t('scrollMessages.scrollToBottom')}
            </Typography>
            <TextField
              label={t('labels.typeInitials')}
              value={privacyInitials}
              onChange={(e) => setPrivacyInitials(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 4 }}
              sx={{ mt: 2, width: 300 }}
            />
            <Box sx={{ mt: 2 }}>
              <TextField
                label={t('labels.forbiddenName')}
                value={privacyForbiddenName}
                onChange={(e) => setPrivacyForbiddenName(e.target.value)}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => openConfirm('privacy')}>{t('buttons.disagree')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConsents((prev) => ({ ...prev, privacyPolicy: true }));
              setPrivacyDisagree(false);
              setPrivacyOpen(false);
            }}
            disabled={!privacyScrolled || privacyInitials.trim().length < 2}
          >
            {t('buttons.iAgree')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notice of Privacy Practices Modal */}
      <Dialog open={noticePrivacyOpen} onClose={closeNoticePrivacyModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('dialogs.noticeOfPrivacyPractices.title')}</DialogTitle>
        <DialogContent dividers>
          <Box
            ref={noticePrivacyRef}
            onScroll={handleNoticePrivacyScroll}
            sx={{ maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap' }}
          >
            {(() => {
              const raw = (noticeOfPrivacyPractices?.text || []).join('\n\n');
              const name = practice || '[Medical Practice]';
              return raw.replace(/\[Medical practice\]|\[Medical Practice\]/g, name);
            })()}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color={noticePrivacyScrolled ? 'success.main' : 'text.secondary'}>
              {noticePrivacyScrolled ? t('scrollMessages.reachedEnd', { type: t('scrollMessages.notice') }) : t('scrollMessages.scrollToBottom')}
            </Typography>
            <TextField
              label={t('labels.typeInitials')}
              value={noticePrivacyInitials}
              onChange={(e) => setNoticePrivacyInitials(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 4 }}
              sx={{ mt: 2, width: 300 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => openConfirm('noticePrivacy')}>{t('buttons.disagree')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConsents((prev) => ({ ...prev, noticeOfPrivacyPractices: true }));
              setNoticePrivacyDisagree(false);
              setNoticePrivacyOpen(false);
            }}
            disabled={!noticePrivacyScrolled || noticePrivacyInitials.trim().length < 2}
          >
            {t('buttons.iAgree')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Imagery (Other Purposes / Social Media) Modal */}
      <Dialog open={imagerySocialOpen} onClose={closeImagerySocialModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('dialogs.imagerySocialMedia.title')}</DialogTitle>
        <DialogContent dividers>
          <Box
            ref={imagerySocialRef}
            onScroll={handleImagerySocialScroll}
            sx={{ maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap' }}
          >
            {(() => {
              const raw = (imagerySocialMedia?.text || []).join('\n\n');
              const name = practice || '[Medical practice]';
              return raw.replace(/\[Medical practice\]|\[Medical Practice\]/g, name);
            })()}
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('dialogs.imagerySocialMedia.deidentifiedNote')}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color={(imagerySocialRequiresScroll ? imagerySocialScrolled : true) ? 'success.main' : 'text.secondary'}>
              {imagerySocialRequiresScroll
                ? (imagerySocialScrolled ? t('scrollMessages.reachedEnd', { type: t('scrollMessages.consent') }) : t('scrollMessages.scrollToBottom'))
                : t('scrollMessages.reviewAndInitials')}
            </Typography>
            <TextField
              label={t('labels.typeInitials')}
              value={imagerySocialInitials}
              onChange={(e) => setImagerySocialInitials(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 4 }}
              sx={{ mt: 2, width: 300 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImagerySocialModal}>{t('buttons.disagree')}</Button>
          <Button
            variant="outlined"
            color="success"
            sx={{
              borderWidth: 2,
              borderStyle: 'solid',
              borderColor: 'success.main',
              textTransform: 'uppercase',
            }}
            onClick={() => {
              setConsents((prev) => ({ ...prev, imagerySocialMedia: true }));
              setImagerySocialDeidentified(true);
              setImagerySocialDisagree(false);
              setImagerySocialOpen(false);
            }}
            disabled={((imagerySocialRequiresScroll && !imagerySocialScrolled)) || imagerySocialInitials.trim().length < 2}
          >
            {t('buttons.iAgreeWithRemoval')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConsents((prev) => ({ ...prev, imagerySocialMedia: true }));
              setImagerySocialDisagree(false);
              setImagerySocialDeidentified(false);
              setImagerySocialOpen(false);
            }}
            disabled={((imagerySocialRequiresScroll && !imagerySocialScrolled)) || imagerySocialInitials.trim().length < 2}
          >
            {t('buttons.iAgree')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Authorized Representatives Modal */}
      <Dialog open={authRepOpen} onClose={closeAuthRepModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('dialogs.authorizedRepresentatives.title')}</DialogTitle>
        <DialogContent dividers>
          <Box
            ref={authRepRef}
            onScroll={handleAuthRepScroll}
            sx={{ maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap' }}
          >
            {(() => {
              const raw = (authorizedRepresentativesText?.text || []).join('\n\n');
              const name = practice || '[Medical practice]';
              return raw.replace(/\[Medical practice\]|\[Medical Practice\]/g, name);
            })()}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color={authRepScrolled ? 'success.main' : 'text.secondary'}>
              {authRepScrolled ? t('scrollMessages.reachedEnd', { type: t('scrollMessages.designation') }) : t('scrollMessages.scrollToBottom')}
            </Typography>
            <TextField
              label={t('labels.typeInitials')}
              value={authRepInitials}
              onChange={(e) => setAuthRepInitials(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 4 }}
              sx={{ mt: 2, width: 300 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => openConfirm('authRep')}>{t('buttons.disagree')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConsents((prev) => ({ ...prev, authorizedRepresentatives: true }));
              setAuthRepDisagree(false);
              setAuthRepOpen(false);
            }}
            disabled={!authRepScrolled || authRepInitials.trim().length < 2}
          >
            {t('buttons.iAgree')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Imagery (Treatment/Education) Modal */}
      <Dialog open={imageryEduOpen} onClose={closeImageryEduModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('dialogs.imageryTreatmentEducation.title')}</DialogTitle>
        <DialogContent dividers>
          <Box
            ref={imageryEduRef}
            onScroll={handleImageryEduScroll}
            sx={{ maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap' }}
          >
            {(() => {
              const raw = (imageryTreatmentEducation?.text || []).join('\n\n');
              const name = practice || '[Medical practice]';
              return raw.replace(/\[Medical practice\]|\[Medical Practice\]/g, name);
            })()}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color={imageryEduScrolled ? 'success.main' : 'text.secondary'}>
              {imageryEduScrolled ? t('scrollMessages.reachedEnd', { type: t('scrollMessages.consent') }) : t('scrollMessages.scrollToBottom')}
            </Typography>
            <TextField
              label={t('labels.typeInitials')}
              value={imageryEduInitials}
              onChange={(e) => setImageryEduInitials(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 4 }}
              sx={{ mt: 2, width: 300 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => openConfirm('imageryEdu')}>{t('buttons.disagree')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConsents((prev) => ({ ...prev, imageryTreatmentEducation: true }));
              setImageryEduDisagree(false);
              setImageryEduOpen(false);
            }}
            disabled={!imageryEduScrolled || imageryEduInitials.trim().length < 2}
          >
            {t('buttons.iAgree')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Records Release Modal */}
      <Dialog open={recordsOpen} onClose={closeRecordsModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('dialogs.recordsRelease.title')}</DialogTitle>
        <DialogContent dividers>
          <Box
            ref={recordsRef}
            onScroll={handleRecordsScroll}
            sx={{ maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap' }}
          >
            {(() => {
              const raw = (recordsRelease?.text || []).join('\n\n');
              const name = practice || '[Medical practice]';
              return raw.replace(/\[Medical practice\]|\[Medical Practice\]/g, name);
            })()}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color={recordsScrolled ? 'success.main' : 'text.secondary'}>
              {recordsScrolled ? t('scrollMessages.reachedEnd', { type: t('scrollMessages.authorization') }) : t('scrollMessages.scrollToBottom')}
            </Typography>
            <TextField
              label={t('labels.typeInitials')}
              value={recordsInitials}
              onChange={(e) => setRecordsInitials(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 4 }}
              sx={{ mt: 2, width: 300 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => openConfirm('records')}>{t('buttons.disagree')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConsents((prev) => ({ ...prev, recordsRelease: true }));
              setRecordsDisagree(false);
              setRecordsOpen(false);
            }}
            disabled={!recordsScrolled || recordsInitials.trim().length < 2}
          >
            {t('buttons.iAgree')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Financial Responsibility Modal */}
      <Dialog open={financialOpen} onClose={closeFinancialModal} maxWidth="md" fullWidth>
        <DialogTitle>{t('dialogs.financialResponsibility.title')}</DialogTitle>
        <DialogContent dividers>
          <Box
            ref={financialRef}
            onScroll={handleFinancialScroll}
            sx={{ maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap' }}
          >
            {(() => {
              const raw = (financialResponsibility?.statement || []).join('\n\n');
              const name = practice || '[Medical practice]';
              return raw.replace(/\[Medical practice\]|\[Medical Practice\]/g, name);
            })()}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color={financialScrolled ? 'success.main' : 'text.secondary'}>
              {financialScrolled ? t('scrollMessages.reachedEnd', { type: t('scrollMessages.statement') }) : t('scrollMessages.scrollToBottom')}
            </Typography>
            <TextField
              label={t('labels.typeInitials')}
              value={financialInitials}
              onChange={(e) => setFinancialInitials(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 4 }}
              sx={{ mt: 2, width: 300 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => openConfirm('financial')}>{t('buttons.disagree')}</Button>
          <Button
            variant="contained"
            onClick={() => {
              setConsents((prev) => ({ ...prev, financialResponsibility: true }));
              setFinancialDisagree(false);
              setFinancialOpen(false);
            }}
            disabled={!financialScrolled || financialInitials.trim().length < 2}
          >
            {t('buttons.iAgree')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disagree Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={closeConfirm} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dialogs.confirmDisagree.title')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {t('dialogs.confirmDisagree.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>{t('buttons.cancel')}</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDisagree}>
            {t('buttons.confirmDisagree')}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('sections.signature')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('signatureSection.instructions')}
        </Typography>
        <TextField
          fullWidth
          label={t('labels.patientFullName')}
          value={patientName}
          onChange={(e) => setPatientName(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{ readOnly: true }}
        />
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('signatureSection.signInstructions')}
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              width: '100%',
              height: 180,
              position: 'relative',
              touchAction: 'none',
            }}
          >
            <canvas
              ref={canvasRef}
              width={600}
              height={180}
              style={{ width: '100%', height: '180px', display: 'block' }}
              onMouseDown={(e) => {
                setIsDrawing(true);
                const rect = e.currentTarget.getBoundingClientRect();
                const ctx = e.currentTarget.getContext('2d');
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.lineCap = 'round';
                ctx.beginPath();
                ctx.moveTo((e.clientX - rect.left) * (e.currentTarget.width / rect.width), (e.clientY - rect.top) * (e.currentTarget.height / rect.height));
              }}
              onMouseMove={(e) => {
                if (!isDrawing) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const ctx = e.currentTarget.getContext('2d');
                ctx.lineTo((e.clientX - rect.left) * (e.currentTarget.width / rect.width), (e.clientY - rect.top) * (e.currentTarget.height / rect.height));
                ctx.stroke();
              }}
              onMouseUp={(e) => {
                if (!isDrawing) return;
                setIsDrawing(false);
                const data = e.currentTarget.toDataURL('image/png');
                setSignatureDataUrl(data);
              }}
              onMouseLeave={(e) => {
                if (!isDrawing) return;
                setIsDrawing(false);
                const data = e.currentTarget.toDataURL('image/png');
                setSignatureDataUrl(data);
              }}
              onTouchStart={(e) => {
                const touch = e.touches[0];
                const canvas = e.currentTarget;
                const rect = canvas.getBoundingClientRect();
                const ctx = canvas.getContext('2d');
                ctx.strokeStyle = '#000';
                ctx.lineWidth = 2;
                ctx.lineCap = 'round';
                ctx.beginPath();
                ctx.moveTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                setIsDrawing(true);
              }}
              onTouchMove={(e) => {
                if (!isDrawing) return;
                const touch = e.touches[0];
                const canvas = e.currentTarget;
                const rect = canvas.getBoundingClientRect();
                const ctx = canvas.getContext('2d');
                ctx.lineTo((touch.clientX - rect.left) * (canvas.width / rect.width), (touch.clientY - rect.top) * (canvas.height / rect.height));
                ctx.stroke();
              }}
              onTouchEnd={(e) => {
                if (!isDrawing) return;
                setIsDrawing(false);
                const data = e.currentTarget.toDataURL('image/png');
                setSignatureDataUrl(data);
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => {
                const canvas = canvasRef.current;
                if (canvas) {
                  const ctx = canvas.getContext('2d');
                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                }
                setSignatureDataUrl('');
              }}
            >
              {t('buttons.clearSignature')}
            </Button>
            {hasSignature ? (
              <Typography variant="caption" color="success.main">{t('signatureSection.signatureCaptured')}</Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">{t('signatureSection.signatureRequired')}</Typography>
            )}
          </Box>
        </Box>
        <TextField
          fullWidth
          type="date"
          label={t('labels.date')}
          InputLabelProps={{ shrink: true }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
          InputProps={{ readOnly: true }}
        />
      </Paper>

          <Button 
            fullWidth 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={!idComplete || !date || !hasSignature || !allReviewed || isSubmitting}
          >
            {isSubmitting ? t('buttons.submitting') : t('buttons.continue')}
          </Button>
        </>
      )}
    </Box>
  );
}
